/**
 * Iter73 — Steps-sync opt-in helper.
 *
 * Some testers don't track their daily steps with a phone/watch and find
 * the Steps energy-refill widget on the Home screen confusing. We ask
 * the user a Yes/No question in the welcome carousel (Android only).
 *
 *   localStorage key: "fitjong_steps_optin"
 *   values: "yes" → show Steps card + banner
 *           "no"  → hide Steps card + banner, keep settings toggle so
 *                   they can opt back in later
 *           null  → unanswered (treated as "yes" for backwards-compat
 *                   with users who never saw the new carousel slide)
 */
import { useEffect, useState } from "react";

const KEY = "fitjong_steps_optin";
// Iter76 — Custom event fired whenever the opt-in flips so React
// components that depend on it can re-render without needing a page
// reload. `storage` events only fire across tabs, never in-tab.
const CHANGE_EVENT = "fitjong:steps-optin-changed";

export function getStepsOptIn() {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "yes" || v === "no") return v;
    return null;
  } catch (_) {
    return null;
  }
}

export function setStepsOptIn(v) {
  try {
    if (v === "yes" || v === "no") localStorage.setItem(KEY, v);
    else localStorage.removeItem(KEY);
  } catch (_) { /* ignore */ }
  // Iter76 — Notify any mounted component (Dashboard, Layout, etc.)
  // that the value just changed.
  try { window.dispatchEvent(new Event(CHANGE_EVENT)); } catch (_) {}
}

/**
 * Returns true when the Steps UI should be visible. Defaults to TRUE
 * when the user hasn't answered yet (so existing users keep their
 * current experience), and FALSE only when they explicitly opted out.
 */
export function stepsUiEnabled() {
  return getStepsOptIn() !== "no";
}

/**
 * React hook — returns the latest opt-in value and re-renders the
 * caller whenever it changes (in this tab or another).
 * Use this in any component that conditionally renders the Steps UI.
 *
 * Iter76e — Moved `import` to top of file and `stepsUiEnabled` above
 * this hook for cleaner module semantics (mid-file imports are valid
 * but trip some older Android-WebView bundler-cache strategies).
 */
export function useStepsUiEnabled() {
  const [enabled, setEnabled] = useState(() => stepsUiEnabled());
  useEffect(() => {
    const tick = () => setEnabled(stepsUiEnabled());
    window.addEventListener(CHANGE_EVENT, tick);
    window.addEventListener("storage", tick);
    return () => {
      window.removeEventListener(CHANGE_EVENT, tick);
      window.removeEventListener("storage", tick);
    };
  }, []);
  return enabled;
}
